import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { Container, Card, Alert, Button, Form, Row, Col } from 'react-bootstrap';

const CookiePolicy: React.FC = () => {
  const isMobile = useIsMobile();
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    advertising: false,
    preferences: false,
  });

  useEffect(() => {
    // Load saved preferences
    const saved = localStorage.getItem('cookiePreferences');
    if (saved) {
      setCookiePreferences(JSON.parse(saved));
    }
  }, []);

  const handleToggle = (key: string) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    setCookiePreferences(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));
  };

  const handleSave = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    // Set cookie expiration
    const expiration = new Date();
    expiration.setFullYear(expiration.getFullYear() + 1);
    document.cookie = `cookieConsent=true; expires=${expiration.toUTCString()}; path=/`;
    alert('Preferences saved!');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      advertising: true,
      preferences: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    const expiration = new Date();
    expiration.setFullYear(expiration.getFullYear() + 1);
    document.cookie = `cookieConsent=true; expires=${expiration.toUTCString()}; path=/`;
    alert('All cookies accepted!');
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      advertising: false,
      preferences: false,
    };
    setCookiePreferences(onlyEssential);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyEssential));
    const expiration = new Date();
    expiration.setFullYear(expiration.getFullYear() + 1);
    document.cookie = `cookieConsent=true; expires=${expiration.toUTCString()}; path=/`;
    alert('Only essential cookies enabled!');
  };

  const content = (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Body className="p-4 p-md-5">
          <h1 className="mb-4">Cookie & Data Consent</h1>

          <div className="text-muted mb-4">
            Last Updated: {new Date().toLocaleDateString()}
          </div>

          <Alert variant="info" className="mb-4">
            We use cookies and similar technologies to enhance your experience, analyze usage,
            and deliver personalized content. You can choose which types of cookies to accept.
          </Alert>

          <section className="mb-5">
            <h2 className="mb-3">What Are Cookies?</h2>
            <p className="mb-3">
              Cookies are small text files stored on your device when you visit websites.
              They help us provide you with a better experience by:
            </p>
            <ul>
              <li>Remembering your login and preferences</li>
              <li>Understanding how you use our Service</li>
              <li>Improving performance and functionality</li>
              <li>Delivering relevant content and advertisements</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-4">Types of Cookies We Use</h2>

            <div className="d-flex flex-column gap-3">
              {/* Essential Cookies */}
              <Card className="border">
                <Card.Body className="p-3">
                  <Row className="align-items-center mb-3">
                    <Col xs="auto">
                      <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-danger">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </Col>
                    <Col className="flex-grow-1">
                      <h3 className="h6 mb-0">Essential Cookies</h3>
                      <small className="text-muted">Always Required</small>
                    </Col>
                    <Col xs="auto">
                      <span className="badge bg-success bg-opacity-10 text-success">Active</span>
                    </Col>
                  </Row>
                  <p className="small mb-0">
                    Required for basic functionality: authentication, security, and core features.
                  </p>
                </Card.Body>
              </Card>

              {/* Analytics Cookies */}
              <Card className="border">
                <Card.Body className="p-3">
                  <Row className="align-items-center mb-3">
                    <Col xs="auto">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-primary">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    </Col>
                    <Col className="flex-grow-1">
                      <h3 className="h6 mb-0">Analytics Cookies</h3>
                      <small className="text-muted">Help us improve</small>
                    </Col>
                    <Col xs="auto">
                      <Form.Check
                        type="switch"
                        checked={cookiePreferences.analytics}
                        onChange={() => handleToggle('analytics')}
                      />
                    </Col>
                  </Row>
                  <p className="small mb-0">
                    Help us understand how users interact with our Service to improve performance.
                  </p>
                </Card.Body>
              </Card>

              {/* Advertising Cookies */}
              <Card className="border">
                <Card.Body className="p-3">
                  <Row className="align-items-center mb-3">
                    <Col xs="auto">
                      <div className="bg-purple bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#6f42c1'}}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a4 4 0 01-1.564-.317z" />
                        </svg>
                      </div>
                    </Col>
                    <Col className="flex-grow-1">
                      <h3 className="h6 mb-0">Advertising Cookies</h3>
                      <small className="text-muted">Personalized ads</small>
                    </Col>
                    <Col xs="auto">
                      <Form.Check
                        type="switch"
                        checked={cookiePreferences.advertising}
                        onChange={() => handleToggle('advertising')}
                      />
                    </Col>
                  </Row>
                  <p className="small mb-0">
                    Deliver relevant advertisements and measure ad campaign effectiveness.
                  </p>
                </Card.Body>
              </Card>

              {/* Preference Cookies */}
              <Card className="border">
                <Card.Body className="p-3">
                  <Row className="align-items-center mb-3">
                    <Col xs="auto">
                      <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-success">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426-1.756-2.924-1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                    </Col>
                    <Col className="flex-grow-1">
                      <h3 className="h6 mb-0">Preference Cookies</h3>
                      <small className="text-muted">Remember settings</small>
                    </Col>
                    <Col xs="auto">
                      <Form.Check
                        type="switch"
                        checked={cookiePreferences.preferences}
                        onChange={() => handleToggle('preferences')}
                      />
                    </Col>
                  </Row>
                  <p className="small mb-0">
                    Remember your language, location, and display preferences.
                  </p>
                </Card.Body>
              </Card>
            </div>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">Third-Party Services</h2>
            <p className="mb-3">
              We may use third-party services that also collect cookies:
            </p>
            <ul>
              <li><strong>Google Analytics:</strong> Usage analytics and insights</li>
              <li><strong>Facebook Pixel:</strong> Ad tracking and conversions</li>
              <li><strong>Google AdSense:</strong> Advertising services</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">Managing Cookies</h2>
            <p className="mb-3">
              You can manage or disable cookies through:
            </p>
            <ul className="mb-3">
              <li>Browser settings (Chrome, Firefox, Safari, Edge)</li>
              <li>Device-level privacy settings</li>
              <li>Opt-out tools provided by advertising networks</li>
              <li>Your preferences on this page</li>
            </ul>
            <p className="mt-3">
              Note that disabling cookies may affect Service functionality.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Changes will be posted on this page
              with an updated revision date. We encourage you to review this policy periodically.
            </p>
          </section>

          {/* Action Buttons */}
          <div className="d-flex flex-wrap gap-2 mt-4 pt-4 border-top">
            <Button variant="primary" onClick={handleAcceptAll}>
              Accept All Cookies
            </Button>
            <Button variant="outline-secondary" onClick={handleRejectAll}>
              Reject Non-Essential
            </Button>
            <Button variant="success" onClick={handleSave}>
              Save Preferences
            </Button>
          </div>

          <section className="mt-4 pt-4 border-top">
            <h2 className="mb-3">Contact Us</h2>
            <p>
              For questions about cookies and data consent, contact us at{' '}
              <a href="mailto:privacy@nayabajar.com" className="text-primary">
                privacy@nayabajar.com
              </a>
            </p>
          </section>
        </Card.Body>
      </Card>
    </Container>
  );

  if (isMobile) {
    return <MobileLayout>{content}</MobileLayout>;
  }

  return <Layout showSidebar={false}>{content}</Layout>;
};

export default CookiePolicy;
